import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useCommentStore } from '@/store/commentStore';
import { useAuthStore } from '@/store/authStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { addComment, updateComment, deleteComment } from '@/api/comments';
import type { Comment } from '@/api/comments';

// ── Helpers ───────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getInitials(email: string): string {
  const parts = email.split('@')[0] ?? '';
  const segments = parts.split(/[._-]/).filter(Boolean);
  if (segments.length >= 2) {
    return (segments[0]![0]! + segments[1]![0]!).toUpperCase();
  }
  return parts.slice(0, 2).toUpperCase();
}

function initialsColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) | 0;
  }
  const colors = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2'];
  return colors[Math.abs(hash) % colors.length]!;
}

// ── Styles ────────────────────────────────────────────────────

// panelStyle is now computed inside CommentPanel to support responsive layout

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 6,
  padding: '10px 12px',
  marginBottom: 6,
};

const btnPrimary: React.CSSProperties = {
  padding: '6px 14px',
  background: 'var(--color-primary-hover)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const btnDisabled: React.CSSProperties = {
  ...btnPrimary,
  background: 'var(--bg-surface-raised)',
  color: 'var(--text-tertiary)',
  cursor: 'default',
};

const btnSecondary: React.CSSProperties = {
  padding: '3px 8px',
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 3,
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

// ── Comment thread component ──────────────────────────────────

interface ThreadProps {
  thread: Comment;
  replies: Comment[];
  currentUserId: string | undefined;
  mapId: string;
  isViewer: boolean;
}

function CommentThread({ thread, replies, currentUserId, mapId, isViewer }: ThreadProps) {
  const { t } = useTranslation();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const addLocalComment = useCommentStore((s) => s.addLocalComment);
  const updateLocalComment = useCommentStore((s) => s.updateLocalComment);
  const removeLocalComment = useCommentStore((s) => s.removeLocalComment);

  const isResolved = thread.resolved;

  const handleResolve = useCallback(async () => {
    try {
      const updated = await updateComment(mapId, thread.commentId, { resolved: !thread.resolved });
      updateLocalComment(thread.commentId, updated);
    } catch (err) {
      console.error('[CommentPanel] Failed to resolve comment:', err);
    }
  }, [mapId, thread, updateLocalComment]);

  const handleReply = useCallback(async () => {
    if (!replyText.trim() || isSending) return;
    setIsSending(true);
    try {
      const created = await addComment(mapId, thread.nodeId, replyText.trim(), thread.commentId);
      addLocalComment(created);
      setReplyText('');
      setShowReplyInput(false);
    } catch (err) {
      console.error('[CommentPanel] Failed to add reply:', err);
    } finally {
      setIsSending(false);
    }
  }, [mapId, thread, replyText, isSending, addLocalComment]);

  const handleDelete = useCallback(async (commentId: string) => {
    try {
      await deleteComment(mapId, commentId);
      removeLocalComment(commentId);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('[CommentPanel] Failed to delete comment:', err);
    }
  }, [mapId, removeLocalComment]);

  const renderComment = (comment: Comment, isReply: boolean) => {
    const ownComment = comment.authorId === currentUserId;
    return (
      <div
        key={comment.commentId}
        style={{
          ...cardStyle,
          marginLeft: isReply ? 20 : 0,
          opacity: isResolved ? 0.5 : 1,
        }}
      >
        {/* Author row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: initialsColor(comment.authorEmail),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {getInitials(comment.authorEmail)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                color: 'var(--text-primary)',
                fontSize: 12,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {comment.authorEmail}
            </div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>{relativeTime(comment.createdAt)}</div>
          </div>
        </div>

        {/* Text */}
        <div style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {comment.text}
        </div>

        {/* Actions */}
        {!isViewer && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {!isReply && (
              <button style={btnSecondary} onClick={() => void handleResolve()}>
                {isResolved ? t('panels.unresolve') : t('panels.resolve')}
              </button>
            )}
            {!isReply && (
              <button style={btnSecondary} onClick={() => setShowReplyInput((v) => !v)}>
                {t('panels.reply')}
              </button>
            )}
            {ownComment && (
              <>
                {confirmDeleteId === comment.commentId ? (
                  <>
                    <button
                      style={{ ...btnSecondary, color: 'var(--text-error)', borderColor: '#7f1d1d' }}
                      onClick={() => void handleDelete(comment.commentId)}
                    >
                      {t('panels.confirm')}
                    </button>
                    <button style={btnSecondary} onClick={() => setConfirmDeleteId(null)}>
                      {t('panels.cancel')}
                    </button>
                  </>
                ) : (
                  <button
                    style={{ ...btnSecondary, color: 'var(--text-error)' }}
                    onClick={() => setConfirmDeleteId(comment.commentId)}
                  >
                    {t('panels.delete')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {renderComment(thread, false)}
      {replies.map((r) => renderComment(r, true))}

      {/* Reply input */}
      {showReplyInput && (
        <div style={{ marginLeft: 20, display: 'flex', gap: 6, marginTop: 4 }}>
          <input
            type="text"
            placeholder={t('panels.replyPlaceholder')}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && replyText.trim()) {
                void handleReply();
              }
              e.stopPropagation();
            }}
            style={{
              flex: 1,
              padding: '6px 8px',
              background: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <button
            onClick={() => void handleReply()}
            disabled={isSending || !replyText.trim()}
            style={isSending || !replyText.trim() ? btnDisabled : btnPrimary}
          >
            {isSending ? '...' : t('panels.reply')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────

interface CommentPanelProps {
  mapId: string | null;
}

export function CommentPanel({ mapId }: CommentPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const commentPanelNodeId = useEditorStore((s) => s.commentPanelNodeId);
  const closeCommentPanel = useEditorStore((s) => s.closeCommentPanel);
  const nodes = useDocumentStore((s) => s.nodes);
  const comments = useCommentStore((s) => s.comments);
  const isLoading = useCommentStore((s) => s.isLoading);
  const addLocalComment = useCommentStore((s) => s.addLocalComment);
  const user = useAuthStore((s) => s.user);

  const [newText, setNewText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const node = commentPanelNodeId ? nodes.get(commentPanelNodeId) : undefined;
  const nodeComments = commentPanelNodeId ? (comments.get(commentPanelNodeId) ?? []) : [];

  // Focus input when panel opens
  useEffect(() => {
    if (commentPanelNodeId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [commentPanelNodeId]);

  // Separate threads (top-level) and replies
  const { repliesByParent, unresolvedFirst } = useMemo(() => {
    const topLevel = nodeComments.filter((c) => !c.parentCommentId);
    const replyMap = new Map<string, Comment[]>();
    for (const c of nodeComments) {
      if (c.parentCommentId) {
        let arr = replyMap.get(c.parentCommentId);
        if (!arr) {
          arr = [];
          replyMap.set(c.parentCommentId, arr);
        }
        arr.push(c);
      }
    }
    // Sort: unresolved first, then resolved
    const sorted = [...topLevel].sort((a, b) => {
      if (a.resolved !== b.resolved) return a.resolved ? 1 : -1;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
    return { repliesByParent: replyMap, unresolvedFirst: sorted };
  }, [nodeComments]);

  // Determine if user is viewer-only (no role info available = assume editor)
  const isViewer = false; // TODO: integrate with sharing role when available

  const handleAddComment = useCallback(async () => {
    if (!newText.trim() || isSending || !commentPanelNodeId || !mapId) return;
    setIsSending(true);
    try {
      const created = await addComment(mapId, commentPanelNodeId, newText.trim());
      addLocalComment(created);
      setNewText('');
    } catch (err) {
      console.error('[CommentPanel] Failed to add comment:', err);
    } finally {
      setIsSending(false);
    }
  }, [newText, isSending, commentPanelNodeId, mapId, addLocalComment]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeCommentPanel();
      }
    },
    [closeCommentPanel],
  );

  if (!commentPanelNodeId || !node) return null;

  const panelStyle: React.CSSProperties = {
    position: isMobile ? 'fixed' : 'absolute',
    top: isMobile ? 0 : 50,
    bottom: isMobile ? 0 : 28,
    right: 0,
    width: isMobile ? '100%' : 340,
    zIndex: isMobile ? 35 : 20,
    background: 'var(--bg-app)',
    borderLeft: isMobile ? 'none' : '1px solid var(--border-subtle)',
    fontFamily: 'var(--font-family)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'commentPanelSlideIn 150ms ease-out',
  };

  return (
    <>
    {isMobile && (
      <div
        onClick={closeCommentPanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-overlay)',
          zIndex: 34,
        }}
      />
    )}
    <div style={panelStyle} onKeyDown={handleKeyDown} tabIndex={-1}>
      <style>{`
        @keyframes commentPanelSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          paddingTop: isMobile ? 'var(--space-4)' : 12,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('panels.comments')}
        </span>
        <button
          onClick={closeCommentPanel}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-lg)',
            padding: '0 4px',
            lineHeight: 1,
          }}
          title={t('panels.closePanel', { panel: 'comment' })}
        >
          x
        </button>
      </div>

      {/* Node title */}
      <div
        style={{
          padding: '8px 16px',
          color: 'var(--text-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {node.text || t('panels.emptyNode')}
      </div>

      {/* Thread list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 12px' }}>
        {isLoading && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'center', padding: 16 }}>
            {t('panels.loadingComments')}
          </div>
        )}

        {!isLoading && unresolvedFirst.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', padding: 16 }}>
            {t('panels.noComments')}
          </div>
        )}

        {unresolvedFirst.map((thread) => (
          <CommentThread
            key={thread.commentId}
            thread={thread}
            replies={repliesByParent.get(thread.commentId) ?? []}
            currentUserId={user?.userId}
            mapId={mapId ?? ''}
            isViewer={isViewer}
          />
        ))}
      </div>

      {/* Add comment input */}
      {!isViewer && (
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            placeholder={t('panels.addComment')}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newText.trim()) {
                void handleAddComment();
              }
              e.stopPropagation();
            }}
            style={{
              flex: 1,
              padding: '8px 10px',
              background: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <button
            onClick={() => void handleAddComment()}
            disabled={isSending || !newText.trim()}
            style={isSending || !newText.trim() ? btnDisabled : btnPrimary}
          >
            {isSending ? '...' : t('panels.comment')}
          </button>
        </div>
      )}
    </div>
    </>
  );
}
